import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { SourceResource } from "@prequel/import-react";
import { useSource } from "@prequel/import-react";
import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type SourceContext = {
  source: SourceResource;
  refreshSource: () => void;
};

export function useSourceContext(): SourceContext {
  return useOutletContext<SourceContext>();
}

const SourcePage = () => {
  const sourceID = useRequiredParam("sourceID");
  const { addToast } = useToast();
  const { data: source, error, isLoading, refetch: refreshSource } = useSource(sourceID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load source." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!source) {
    return <Navigate to="/import/sources" replace />;
  }

  return <Outlet context={{ source, refreshSource }} />;
};

export const SourceDetailsPage = () => {
  const navigate = useNavigate();
  const { source, refreshSource } = useSourceContext();
  const basePath = `/import/sources/${source.id}`;

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} end>
      Overview
    </NavLink>,
    <NavLink key="Connection" to={`${basePath}/connection`} className={checkTabIsActive} end>
      Connection
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={source.name}
      onGoBack={() => navigate("/import/sources")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={source.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ source, refreshSource }} />
    </DetailPageLayout>
  );
};

export default SourcePage;
